import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArrowLeft, Flask, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const PHASE_LABELS = {
    preparacion: "Preparación",
    recuperacion: "Recuperación inmediata",
    mantenimiento: "Mantenimiento",
};

export default function TreatmentDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [t, setT] = useState(null);
    const [compoundsBySlug, setCompoundsBySlug] = useState({});

    useEffect(() => {
        Promise.all([api.get(`/treatments/${slug}`), api.get("/compounds")]).then(([a, b]) => {
            setT(a.data);
            setCompoundsBySlug(Object.fromEntries(b.data.map((c) => [c.slug, c])));
        });
    }, [slug]);

    const del = async () => {
        if (!window.confirm("¿Eliminar este tratamiento?")) return;
        await api.delete(`/treatments/${slug}`);
        toast.success("Tratamiento eliminado");
        navigate("/tratamientos");
    };

    if (!t) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Tratamiento · ${t.category}`}
                title={t.name}
                lead={t.description}
                actions={
                    <div className="flex items-center gap-2">
                        <Link to={`/tratamientos/${slug}/editar`} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2" data-testid="treatment-edit-button"><PencilSimple size={14}/> Editar</Link>
                        <button onClick={del} className="px-3.5 py-2 border hairline bg-white text-sm hover:bg-[var(--cream)] text-[var(--terra-2)] inline-flex items-center gap-2" data-testid="treatment-delete-button"><Trash size={14}/> Eliminar</button>
                    </div>
                }
            >
                <div className="mt-4">
                    <Link to="/tratamientos" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14} /> Volver a tratamientos
                    </Link>
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-10 max-w-6xl space-y-10">
                {Object.entries(t.recommended_phases || {}).map(([phase, slugs]) => (
                    <section key={phase} data-testid={`phase-section-${phase}`}>
                        <div className="flex items-end justify-between mb-4">
                            <div>
                                <div className="label-eyebrow mb-1">Fase</div>
                                <h2 className="font-display text-3xl tracking-tight">{PHASE_LABELS[phase] || phase}</h2>
                            </div>
                            <Link
                                to={`/formulas/nuevo?preferred=${slugs.join(",")}`}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid={`generate-formula-${phase}`}
                            >
                                <Flask size={14} weight="bold"/> Generar protocolo
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border hairline">
                            {slugs.map((s) => {
                                const c = compoundsBySlug[s];
                                if (!c) return null;
                                return (
                                    <Link
                                        key={s}
                                        to={`/compuestos/${s}`}
                                        className="bg-white p-5 hover:bg-[var(--cream)] transition-colors"
                                        data-testid={`phase-compound-${s}`}
                                    >
                                        <div className="font-medium leading-snug">{c.name}</div>
                                        <div className="text-xs text-[var(--ink-2)] mt-1">{(c.indications || []).slice(0, 2).join(" · ")}</div>
                                        <div className="text-xs mono text-[var(--ink-3)] mt-2">{(c.evidence || []).length} ref.</div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}
